import React, { useEffect, useMemo } from 'react';
import { AvDialog } from '@fabric-ui/components';
import { FailFull as Error, WarningFullOrange as Warn } from '@fabric-ui/icons/colorful';
import { flex } from '@fabric-ui/theme';
import { Box, Button, Divider, Typography } from '@mui/material';
import { useAvContext } from '../../../context/AvContextProvider';

export enum ValidationSeverity {
  Error = 'ERROR',
  Warning = 'WARNING',
}

export interface ValidationMessage {
  message: string;
  severity?: ValidationSeverity;
  type?: string;
  entity?: string;
  field?: string;
}

export interface MappingValidationDialogProps {
  messages?: ValidationMessage[];
  isOpen: boolean;
  onClose: () => void;
  onContinue?: () => void;
  onUnmount?: () => void;
}

const Message = ({ message: { type, field, entity, message } }: { message: ValidationMessage }) => (
  <div>
    {type && (
      <div>
        <Box component="span" sx={{ fontWeight: 600, mr: '4px' }}>
          {type}:
        </Box>
        {field}
        {field && ' | '}
        {entity}
      </div>
    )}
    <div>{message}</div>
  </div>
);

export const MappingValidationDialog = ({ messages, isOpen, onClose, onContinue, onUnmount }: MappingValidationDialogProps) => {
  const {
    accountEntities: { oldIngressProjs: entityFields },
  } = useAvContext();

  const parseMessage = ({ entity, field, ...message }: ValidationMessage): ValidationMessage => ({
    ...message,
    entity,
    field: entity && field && (entityFields[entity]?.nameMap[field] ?? field),
  });

  const errors = useMemo<ValidationMessage[]>(
    () => messages?.filter(({ severity }) => !severity || severity === ValidationSeverity.Error).map(parseMessage) ?? [],
    [messages]
  );

  const warnings = useMemo<ValidationMessage[]>(
    () => messages?.filter(({ severity }) => severity === ValidationSeverity.Warning).map(parseMessage) ?? [],
    [messages]
  );

  const hasErrors = errors && errors.length > 0;
  const hasWarnings = warnings && warnings.length > 0;

  useEffect(() => onUnmount, []);

  if (!hasErrors && !hasWarnings) {
    return null;
  }

  return (
    <AvDialog
      title="Mapping Validation"
      open={isOpen}
      noPadding
      onClose={onClose}
      footer={
        <Box sx={{ ...flex.justifyEndCenter, gap: 2 }}>
          {!hasErrors && <Button onClick={onContinue}>Continue Anyway</Button>}
          <Button onClick={onClose} variant="contained" color="primary">
            Edit Mapping
          </Button>
        </Box>
      }>
      <Box sx={{ mx: '24px' }}>
        {hasErrors && (
          <Box sx={{ mt: '8px' }}>
            <Typography variant="h8" sx={{ ...flex.row, gap: 1, color: 'var(--color-content-base-primary)' }}>
              <Error />
              Errors
            </Typography>
            <Box sx={{ ...flex.col, gap: 2, mt: '8px' }}>
              {errors?.map(message => (
                <Message key={`${message.entity}-${message.field}`} message={message} />
              ))}
            </Box>
          </Box>
        )}
        {hasErrors && hasWarnings && <Divider sx={{ my: '20px' }} />}
        {hasWarnings && (
          <Box sx={{ mt: '8px' }}>
            <Typography variant="h8" sx={{ ...flex.row, gap: 1, color: 'var(--color-content-base-primary)' }}>
              <Warn />
              Warnings
            </Typography>
            <Box sx={{ ...flex.col, gap: 2, mt: '8px' }}>
              {warnings?.map(message => (
                <Message key={`${message.entity}-${message.field}`} message={message} />
              ))}
            </Box>
          </Box>
        )}
      </Box>
    </AvDialog>
  );
};
